/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Mini Audio Waveform Visualizer Component (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useMemo } from 'react';

interface MiniWaveformProps {
  isPlaying: boolean;
  barCount?: number;
  className?: string;
  progress?: number; // 0 to 100
}

export const MiniWaveform: React.FC<MiniWaveformProps> = ({
  isPlaying,
  barCount = 18,
  className = '',
  progress = 0,
}) => {
  // Deterministic seed bar heights for natural voice envelope
  const staticHeights = useMemo(() => {
    const pattern = [25, 45, 70, 95, 60, 40, 85, 100, 75, 50, 90, 65, 45, 80, 55, 35, 60, 30];
    return Array.from({ length: barCount }, (_, i) => pattern[i % pattern.length] || 50);
  }, [barCount]);

  return (
    <div className={`flex items-center gap-[2px] h-6 overflow-hidden ${className}`}>
      {staticHeights.map((baseHeight, idx) => {
        const isPassed = (idx / barCount) * 100 <= progress;
        // Animation delay for wave-like motion
        const animDelay = (idx * 0.08).toFixed(2);

        return (
          <div
            key={idx}
            className={`w-[3px] rounded-full transition-all duration-150 ${
              isPassed
                ? 'bg-primary'
                : 'bg-surface-border group-hover:bg-foreground-muted/40'
            } ${isPlaying ? 'animate-pulse' : ''}`}
            style={{
              height: isPlaying ? `${Math.max(20, (baseHeight * (0.6 + Math.sin(idx + Date.now() / 300) * 0.4)))}%` : `${baseHeight}%`,
              animationDelay: `${animDelay}s`,
              animationDuration: '0.9s',
            }}
          />
        );
      })}
    </div>
  );
};
